import { useFormik } from "formik";
import { useEffect, useState } from "react";
import * as Yup from "yup";

import { Button, Flex, Input } from "antd";
import { useGetProfile } from "../../../api/api.get";
import { useUpdateProfile } from "../../../api/api.update";
import { useNotifications2 } from "../../../components/notifications/notification";
import { useAuthContext } from "../../../providers/auth.provider";
import { formatBlankWithDefault } from "../../../utils/functions";

export default function AddressSection({ areaCode }: { areaCode?: string }) {
  const { user: userAuth } = useAuthContext();
  const { data, refetch } = useGetProfile();
  const [isEditMode, setIsEditMode] = useState(false);

  useEffect(() => {
    if (userAuth) {
      refetch();
    }
  }, [userAuth]);

  const user = data;

  const [isLoading, setIsLoading] = useState(false);

  const { mutateAsync: updateAddressAsync } = useUpdateProfile();
  const { notification, notifySuccess, notifyError } = useNotifications2();

  const formik = useFormik({
    initialValues: {
      street1: user?.street1 ?? "",
      street2: user?.street2 ?? "",
      city: user?.city ?? "",
      state: user?.state ?? "",
      zipCode: user?.zipCode ?? "",
    },
    validationSchema: Yup.object({
      zipCode: Yup.string()
        .matches(/^[0-9]+$/, "Please enter only numbers.")
        .required("Please enter zipcode."),
      state: Yup.string().required("Please enter state."),
      street1: Yup.string().required("Please enter street address"),
      city: Yup.string().required("Please enter city"),
    }),
    enableReinitialize: true,
    validateOnBlur: true,
    validateOnChange: true,
    onSubmit: async (values) => {
      setIsLoading(true);

      updateAddressAsync(values)
        .then(() => {
          // notifySuccess({ title: 'Success', description: 'Address successfully updated.'})
          setIsEditMode(false);
        })
        .catch((err) => {
          if (err.response.status === 409) {
            notifyError(
              err.response.data?.message ?? "Whoops! Please try again"
            );
          } else {
            notifyError("Whoops! Something went wrong. Please try again.");
          }
        })
        .finally(() => {
          setIsLoading(false);
        });
    },
  });

  return (
    <div className="">
      {notification}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
          <h2 className="text-xl font-bold text-gray-900">Address</h2>
          <p className="text-sm text-gray-600 mt-1">Your address details</p>
        </div>

        <form style={{ paddingLeft: 30, paddingRight: 30, paddingBottom: 30 }}>
          <div className="grid md:grid-cols-2 gap-6 mt-6 mb-4">
            {/* First Name */}
            <div className="space-y-2 flex flex-col">
              <span className="text-gray-700">Street 1</span>
              {isEditMode && (
                <Input
                  status={formik.errors.street1 ? "error" : undefined}
                  {...formik.getFieldProps("street1")}
                />
              )}
              {!isEditMode && (
                <div className="h-10 flex items-center px-3 bg-gray-50 rounded-md border border-gray-300 text-gray-700">
                  {formatBlankWithDefault(formik.values.street1, "-", true)}
                </div>
              )}
            </div>

            <div className="space-y-2 flex flex-col">
              <span className="text-gray-700">Street 2</span>
              {isEditMode && (
                <Input
                  status={formik.errors.street2 ? "error" : undefined}
                  {...formik.getFieldProps("street2")}
                />
              )}
              {!isEditMode && (
                <div className="h-10 flex items-center px-3 bg-gray-50 rounded-md border border-gray-300 text-gray-700">
                  {formatBlankWithDefault(formik.values.street2, "", true)}
                </div>
              )}
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mt-6 mb-4">
            {/* City */}
            <div className="space-y-2 flex flex-col">
              <span className="text-gray-700">City</span>
              {isEditMode && (
                <Input
                  status={formik.errors.city ? "error" : undefined}
                  {...formik.getFieldProps("city")}
                />
              )}
              {!isEditMode && (
                <div className="h-10 flex items-center px-3 bg-gray-50 rounded-md border border-gray-300 text-gray-700">
                  {formatBlankWithDefault(formik.values.city, "-", true)}
                </div>
              )}
            </div>

            <div className="space-y-2 flex flex-col">
              <span className="text-gray-700">State</span>
              {isEditMode && (
                <Input
                  status={formik.errors.state ? "error" : undefined}
                  {...formik.getFieldProps("state")}
                />
              )}
              {!isEditMode && (
                <div className="h-10 flex items-center px-3 bg-gray-50 rounded-md border border-gray-300 text-gray-700">
                  {formatBlankWithDefault(formik.values.state, "-", true)}
                </div>
              )}
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mt-6 mb-4">
            {/* ZipCode */}
            <div className="space-y-2 flex flex-col">
              <span className="text-gray-700">ZipCode</span>
              {isEditMode && (
                <Input
                  status={formik.errors.zipCode ? "error" : undefined}
                  {...formik.getFieldProps("zipCode")}
                />
              )}
              {!isEditMode && (
                <div className="h-10 flex items-center px-3 bg-gray-50 rounded-md border border-gray-300 text-gray-700">
                  {formatBlankWithDefault(formik.values.zipCode, "-", true)}
                </div>
              )}
            </div>
          </div>

          <div className="w-full flex justify-end">
            {isEditMode && (
              <Flex gap={30}>
                <Button type="link" onClick={() => setIsEditMode(false)}>
                  Cancel
                </Button>
                <Button
                  id="update-address"
                  type="primary"
                  disabled={Object.keys(formik.errors).length > 0 || isLoading}
                  loading={isLoading}
                  onClick={formik.submitForm}
                >
                  Update
                </Button>
              </Flex>
            )}
            {!isEditMode && (
              <Button
                type="link"
                id="edit-address-btn"
                onClick={() => setIsEditMode(true)}
              >
                Edit Address Info
              </Button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}
